import { observer } from "mobx-react-lite";
import { TestSelection } from "models/test-run";
import { useEffect, useState } from "react";
import { Icon, Message, Segment } from "semantic-ui-react";
import { TestRunStore } from "stores";
import Spinner from "components/spinner";

interface Props {
  testRunId: number;
}

const TestSelectionInfo = observer(({ testRunId }: Props) => {
  const [testSelection, setTestSelection] = useState<TestSelection | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchTestSelection = async () => {
      try {
        setLoading(true);
        const data = await TestRunStore.getTestSelection(testRunId);
        setTestSelection(data);
        setError(!data);
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchTestSelection();
  }, [testRunId]);

  if (loading) {
    return <Spinner size={16} />;
  }

  if (error || !testSelection) {
    return null;
  }

  return (
    <Segment>
      <h3>
        <Icon name="code branch" color="blue" />
        Test Selection Applied
      </h3>

      {testSelection.hasRules ? (
        <>
          {testSelection.matchedRules.length > 0 ? (
            <>
              <Message info>
                <Message.Header>
                  {testSelection.matchedRules.length} Rule{testSelection.matchedRules.length !== 1 ? 's' : ''} Matched
                </Message.Header>
                <p>
                  {testSelection.matchedRules.length} of {testSelection.rules.length} selection rule{testSelection.rules.length !== 1 ? 's' : ''} matched the changed files.
                </p>
              </Message>

              {testSelection.changedFiles && testSelection.changedFiles.length > 0 && (
                <div style={{ marginTop: '1em' }}>
                  <h4>Changed Files ({testSelection.changedFiles.length}):</h4>
                  <Segment>
                    <ul style={{ listStyleType: 'none', padding: 0, margin: 0 }}>
                      {testSelection.changedFiles.slice(0, 10).map((file, idx) => (
                        <li key={idx} style={{ padding: '0.25em 0' }}>
                          <Icon name="file code outline" />
                          <code style={{ color: '#333' }}>{file}</code>
                        </li>
                      ))}
                      {testSelection.changedFiles.length > 10 && (
                        <li style={{ padding: '0.25em 0', fontStyle: 'italic', color: '#666' }}>
                          ... and {testSelection.changedFiles.length - 10} more file{testSelection.changedFiles.length - 10 !== 1 ? 's' : ''}
                        </li>
                      )}
                    </ul>
                  </Segment>
                </div>
              )}

              <div style={{ marginTop: '1em' }}>
                <h4>Matched Rules:</h4>
                {testSelection.matchedRules.map((rule, idx) => (
                  <Segment key={idx} style={{ marginBottom: '1em' }}>
                    <div style={{ marginBottom: '0.5em' }}>
                      <Icon name="check circle" color="green" />
                      <strong>Rule {idx + 1}:</strong> {rule.displayName}
                    </div>
                    <div style={{ marginLeft: '1.5em', color: '#666' }}>
                      <Icon name="arrow circle right" />
                      {rule.actionDescription}
                    </div>
                  </Segment>
                ))}
              </div>
            </>
          ) : (
            <Message warning>
              <Message.Header>No Rules Matched</Message.Header>
              <p>
                Test selection was enabled with {testSelection.rules.length} rule{testSelection.rules.length !== 1 ? 's' : ''}, but none matched the changed files. No tests were run.
              </p>
              {testSelection.changedFiles && testSelection.changedFiles.length > 0 && (
                <div style={{ marginTop: '1em' }}>
                  <strong>Changed Files:</strong>
                  <ul>
                    {testSelection.changedFiles.slice(0, 5).map((file, idx) => (
                      <li key={idx}><code>{file}</code></li>
                    ))}
                    {testSelection.changedFiles.length > 5 && (
                      <li>... and {testSelection.changedFiles.length - 5} more</li>
                    )}
                  </ul>
                </div>
              )}
            </Message>
          )}
        </>
      ) : (
        <Message warning>
          <Message.Header>No Rules Configured</Message.Header>
          <p>
            Test selection was enabled, but no rules were found in your testery.yml configuration.
          </p>
        </Message>
      )}
    </Segment>
  );
});

export default TestSelectionInfo;
